"use client";

import Link from "next/link";
import Image from "next/image";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { StructuredData } from "@/components/common";
import { AnthropicPartnerBadge } from "@/components/ui/anthropic-partner-badge";
import { defaultLocale, type Locale } from "@/lib/i18n";
import {
  getUseCaseContent,
  patternLabels,
} from "@/content/agent-use-cases/types";
import { getUseCasesForIndustry } from "@/content/industries";
import type { IndustryPage } from "@/content/industries/types";

export default function IndustryAgentPage({
  page,
  locale = defaultLocale,
}: {
  page: IndustryPage;
  locale?: Locale;
}) {
  const c = page.content[locale] ?? page.content[defaultLocale];
  const useCases = getUseCasesForIndustry(page);
  // Localise internal links: default locale is un-prefixed, ms/zh are /{locale}-prefixed.
  const L = (path: string) =>
    locale === defaultLocale ? path : `/${locale}${path}`;

  const prefersReducedMotion = useReducedMotion();
  // Hero entrance moves only: opacity stays 1 so the hero paints before
  // hydration (LCP).
  const reveal = (delay: number) => ({
    initial: prefersReducedMotion ? false : { y: 12 },
    animate: { y: 0 },
    transition: { duration: 0.55, delay, ease: [0.21, 0.6, 0.35, 1] as const },
  });
  const inView = {
    initial: prefersReducedMotion ? false : { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.5, ease: [0.21, 0.6, 0.35, 1] as const },
  };

  return (
    <div className="bg-background">
      {c.faqs.length > 0 && (
        <StructuredData
          type="faq"
          faqs={c.faqs.map((f) => ({ question: f.question, answer: f.answer }))}
        />
      )}

      {/* Hero */}
      <section className="bg-background pt-24 pb-12 sm:pt-28 lg:pt-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <motion.div {...reveal(0)}>
              <nav className="mb-5 font-mono text-xs uppercase tracking-[0.25em] text-neutral-muted">
                <Link href={L("/")} className="transition-colors hover:text-primary">
                  Home
                </Link>
                <span className="mx-2" aria-hidden>
                  /
                </span>
                <Link
                  href={L("/solutions/agent-use-cases/")}
                  className="transition-colors hover:text-primary"
                >
                  Agent use cases
                </Link>
                <span className="mx-2" aria-hidden>
                  /
                </span>
                <span className="text-neutral-emphasis">
                  {page.cardLabel[locale] ?? page.cardLabel[defaultLocale]}
                </span>
              </nav>
              <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-primary">
                {c.eyebrow}
              </p>
              <h1 className="font-display text-4xl font-bold leading-[1.1] tracking-tight text-neutral-emphasis md:text-5xl">
                {c.h1}
              </h1>
              <p className="mt-6 max-w-xl text-lg leading-relaxed text-neutral-muted">
                {c.intro}
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center">
                <Link
                  href={L("/contact/")}
                  data-track-event="cta_click"
                  data-track-location={`industry_${page.key.replace(/-/g, "_")}_hero`}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary-emphasis"
                >
                  {c.ctaLabel}
                  <ArrowRight className="h-4 w-4" aria-hidden />
                </Link>
                <Link
                  href={L("/solutions/agent-use-cases/")}
                  className="group inline-flex items-center justify-center gap-2 text-sm font-semibold text-neutral-emphasis transition hover:text-primary"
                >
                  See every use case
                  <ArrowRight
                    className="h-4 w-4 transition-transform group-hover:translate-x-1"
                    aria-hidden
                  />
                </Link>
              </div>
            </motion.div>

            <motion.div {...reveal(0.12)} className="relative">
              <div className="overflow-hidden rounded-3xl border border-border shadow-lg">
                <Image
                  src={page.heroImage}
                  alt={`${c.h1} — Anchor Sprint`}
                  width={1536}
                  height={1024}
                  priority
                  className="h-full w-full object-cover"
                />
              </div>
              <AnthropicPartnerBadge
                variant="hero"
                className="absolute bottom-4 left-4 z-10"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Industry pain / business model */}
      <section className="bg-neutral-surface py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.h2
            {...inView}
            className="mb-8 font-display text-2xl font-semibold text-neutral-emphasis sm:text-3xl"
          >
            {c.painHeading}
          </motion.h2>
          <div className="grid gap-4 sm:grid-cols-2">
            {c.painPoints.map((p, i) => (
              <motion.div
                key={p.title}
                {...inView}
                transition={{ ...inView.transition, delay: i * 0.05 }}
                className="rounded-2xl border border-border bg-card p-6"
              >
                <h3 className="font-display text-lg font-semibold text-neutral-emphasis">
                  {p.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-neutral-muted">
                  {p.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Where agents pay off — the mapped use cases */}
      <section className="bg-background py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div {...inView}>
            <h2 className="font-display text-2xl font-semibold text-neutral-emphasis sm:text-3xl">
              {c.useCasesHeading}
            </h2>
            <p className="mt-3 max-w-2xl text-base leading-relaxed text-neutral-muted">
              {c.useCasesIntro}
            </p>
          </motion.div>
          <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {useCases.map((useCase) => {
              const uc = getUseCaseContent(useCase, locale);
              const { structure } = useCase;
              return (
                <motion.article
                  key={structure.slug}
                  {...inView}
                  className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-lg"
                >
                  {structure.cardImage && (
                    <div className="relative aspect-[16/10] overflow-hidden bg-neutral-surface">
                      <img
                        src={structure.cardImage}
                        alt=""
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                        width={1536}
                        height={1024}
                        decoding="async"
                      />
                    </div>
                  )}
                  <div className="flex flex-1 flex-col p-6">
                    <span className="mb-2 w-fit rounded-full bg-primary/10 px-2.5 py-0.5 font-mono text-[10px] font-medium text-primary">
                      {patternLabels[structure.pattern]}
                    </span>
                    <h3 className="font-display text-lg font-semibold text-neutral-emphasis">
                      {uc.title}
                    </h3>
                    <p className="mt-2 flex-1 text-sm leading-relaxed text-neutral-muted">
                      {uc.cardSummary}
                    </p>
                    {structure.route && (
                      <Link
                        href={L(`/${structure.route}/`)}
                        className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary transition hover:text-primary-emphasis"
                      >
                        See how we build it
                        <ArrowRight className="h-3.5 w-3.5" aria-hidden />
                      </Link>
                    )}
                  </div>
                </motion.article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Depth sections (why agents fit / why Anchor Sprint) */}
      {c.sections && c.sections.length > 0 && (
        <section className="bg-neutral-surface py-16 lg:py-20">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            {c.sections.map((s) => (
              <motion.div key={s.heading} {...inView} className="mb-12 last:mb-0">
                <h2 className="mb-3 font-display text-2xl font-semibold text-neutral-emphasis">
                  {s.heading}
                </h2>
                <p className="text-base leading-relaxed text-neutral-muted">
                  {s.body}
                </p>
                {s.bullets && (
                  <ul className="mt-4 space-y-2">
                    {s.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-3">
                        <Check
                          className="mt-1 h-4 w-4 shrink-0 text-primary"
                          aria-hidden
                        />
                        <span className="text-sm leading-relaxed text-neutral-muted">
                          {b}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* FAQ */}
      {c.faqs.length > 0 && (
        <section className="bg-background py-16">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            <h2 className="mb-8 font-display text-2xl font-semibold text-neutral-emphasis sm:text-3xl">
              Common questions
            </h2>
            <dl className="space-y-6">
              {c.faqs.map((f) => (
                <div key={f.question}>
                  <dt className="font-display text-base font-semibold text-neutral-emphasis lg:text-lg">
                    {f.question}
                  </dt>
                  <dd className="mt-1.5 text-sm leading-relaxed text-neutral-muted">
                    {f.answer}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </section>
      )}

      {/* Closing CTA */}
      <section className="bg-neutral-emphasis py-16 lg:py-20">
        <div className="mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
          <motion.h2
            {...inView}
            className="font-display text-2xl font-bold text-primary-foreground sm:text-3xl"
          >
            {c.ctaTitle}
          </motion.h2>
          <motion.p
            {...inView}
            className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-primary-foreground/80"
          >
            {c.ctaBody}
          </motion.p>
          <motion.div
            {...inView}
            className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row"
          >
            <Link
              href={L("/contact/")}
              data-track-event="cta_click"
              data-track-location={`industry_${page.key.replace(/-/g, "_")}_footer`}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary-emphasis"
            >
              {c.ctaLabel}
              <ArrowRight className="h-4 w-4" aria-hidden />
            </Link>
            <Link
              href={L("/tools/claude-cost-calculator/")}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-primary-foreground/25 px-6 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary-foreground/10"
            >
              Estimate the cost
              <ArrowRight className="h-4 w-4" aria-hidden />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
